import React from 'react';
import cx from 'classnames';

import styles from './Card.module.scss';
import useUniqueId from '../utils/useUniqueId';
import Box from '../Box';

export interface Props {
  /** CSS className to apply to the card's outer wrapper */
  className?: Parameters<typeof cx>[0];
  /** Content slot. Allows an arbitrary JSX component or block to be placed at the top leading edge of the card (left side for LTR reading languages)  */
  leadingObject?: React.ReactNode;
  /** Content slot. Allows one or more arbitrary JSX components or blocks to be placed at the top of the card next to the leadingObject  */
  headerLeadingContentRows?: React.ReactNode;
  /** Content slot. Allows an arbitrary JSX component or block to be placed at the top trailing edge of the card (right side for LTR reading languages)  */
  headerTrailingObject?: React.ReactNode;
  /** Content slot. Allows one or more arbitrary JSX components or blocks to be placed in the main content section of the card, beneath the header rows  */
  body?: React.ReactNode;
  /** CSS className to apply to the `body` content slot */
  bodyClassName?: Parameters<typeof cx>[0];
  /** Content slot. Allows one or more arbitrary JSX components or blocks to be placed at the bottom of the card. */
  footer?: React.ReactNode;
  /** CSS className to apply to the `footer` content slot */
  footerClassName?: Parameters<typeof cx>[0];
}

export const Card: React.FC<Props> = ({
  className,
  leadingObject,
  headerLeadingContentRows,
  headerTrailingObject,
  body,
  bodyClassName,
  footer,
  footerClassName,
}) => {
  const leadingContentRowBase = useUniqueId('leading-content-row');
  return (
    <Box className={cx(styles.card, className)} gap={3} p={3}>
      <div className={styles.header}>
        {leadingObject && leadingObject}
        {headerLeadingContentRows && (
          <div className={styles.headerLeadingContentRows}>
            {React.Children.map(
              headerLeadingContentRows,
              (row: React.ReactNode, idx: number) => (
                <div
                  key={`${leadingContentRowBase}-${idx}`}
                  data-testid={`${leadingContentRowBase}-${idx}`}
                >
                  {row}
                </div>
              ),
            )}
          </div>
        )}
        {headerTrailingObject && (
          <div className={styles.headerTrailingObject}>
            {headerTrailingObject}
          </div>
        )}
      </div>
      {body && (
        <div className={styles.body}>
          <div className={cx(bodyClassName)}>{body}</div>
        </div>
      )}
      {footer && (
        <div className={cx(styles.footer, footerClassName)}>{footer}</div>
      )}
    </Box>
  );
};

Card.displayName = 'Card';
export default Card;
export { styles };
